'use strict';

define('vb/private/stateManagement/applicationExtension',[
  'vb/helpers/mixin',
  'vb/private/constants',
  'vb/private/services/extensionServices',
  'vb/private/stateManagement/containerRjsInitMixin',
  'vb/private/stateManagement/flowExtension',
], (
  Mixin,
  Constants,
  ExtensionServices,
  ContainerRjsInitMixin,
  FlowExtension,
) => {
  /**
   * @typedef {import('vb/private/services/services').default} Services
   */

  class ApplicationExtension extends Mixin(FlowExtension).with(ContainerRjsInitMixin) {
    /**
     * Creates an instance of ApplicationExtension.
     *
     * @param {Object} extension - The extension object.
     * @param {String} path - The path of the extension.
     * @param {Object} base - The base object for the extension.
     * @param {String} [className='ApplicationExtension'] - The class name for the extension.
     */
    constructor(extension, path, base, className = 'ApplicationExtension') {
      super(extension, path, base, className);

      this._services = null; // Initialized in services getter
      this._serviceModelFiles = null;
    }

    /**
     * The same class is used to extend an application extension.
     *
     * @type {typeof ApplicationExtension} The ApplicationExtension class itself.
     */
    static get extensionClass() {
      return ApplicationExtension;
    }

    /**
     * The default event prefix is the lowercase class name (see container.js) but for
     * application extension we want to use the same event prefix as application.
     *
     * @type {String} The event prefix for the application extension.
     */
    // eslint-disable-next-line class-methods-use-this
    get eventPrefix() {
      return 'application';
    }

    /**
     * Return an array of all Services objects; currently only one.
     *
     * @returns {Array<Services>} An array containing the Services objects associated with the application extension.
     */
    getAllServices() {
      return [this.services];
    }

    /**
     * This is called by Application, during its createServices.
     * Cannot be called in load(), because load() is only called when the app-flow-x file exists.
     *
     * @type {ExtensionServices} The instance of ExtensionServices for this application extension.
     */
    get services() {
      // Registry refreshes replace the extension file list. Recreate the services model so newly discovered
      // service descriptors are available after a partial-digest update or full-manifest recovery.
      if (this._services && this._serviceModelFiles !== this.extension.files) {
        this._services = null;
      }

      if (!this._services) {
        const relativePath = this.getResourceFolder();

        const serviceFileMap = this.definition.services || {};

        const options = {
          application: this.application,
          namespace: this.extensionId,
          relativePath,
          serviceFileMap,
          expressionContext: this.getAvailableContexts(),
          isUnrestrictedRelative: true,
          protocolRegistry: this.application.protocolRegistry,
          extensionRegistryGetter: () => this.application.extensionRegistry,
        };

        // creates an ExtensionService
        this._services = this.extension.loadServicesModel(options, ExtensionServices);
        this._serviceModelFiles = this.extension.files;
      }
      return this._services;
    }

    /**
     * Returns a scope resolver map where keys are scope names ("global" or "application")
     * and values are the matching objects. This is used to build the scopeResolver object.
     *
     * @returns {Object} An object where keys are scope names and values are the corresponding objects.
     * @property {ApplicationExtension} global The global scope resolver object.
     * @property {ApplicationExtension} application The application scope resolver object.
     */
    getScopeResolverMap() {
      return {
        [Constants.GLOBAL_PREFIX]: this,
        [Constants.APPLICATION_PREFIX]: this,
      };
    }
  }

  return ApplicationExtension;
});

